import { FC, useContext, useState, useEffect, useRef, forwardRef, ReactElement, Ref } from "react";
import ConfigurationFormEditContext, { IConfigurationFormEditContext } from '@/contexts/forms/ConfigurationFormEditContext';
import ConfigurationFormEditEvent from "./ConfigurationFormEditEvent";
import ConfigurationFormEditForm from "./ConfigurationFormEditForm";
import ConfigurationFormEditVersion from "./ConfigurationFormEditVersion";
import ConfigurationFormEditSection from "./ConfigurationFormEditSection";
import ConfigurationFormEditField from "./ConfigurationFormEditField";
import { Box, Button, colors, Dialog, Grid, Paper, Slide, styled } from "@mui/material";
import ConfigurationFormEditFieldValue from "./ConfigurationFormEditFieldValue";
import { FormSubmitButton } from "@ngt/forms";
import ConfigurationFormPreview from "./ConfigurationFormPreview";
import { ITrial, ITrialFormMetadata } from "@/api/dtos";
import { ConfigurationType, IConfigurationEvent, IConfigurationField, IConfigurationForm, IConfigurationSection, IConfigurationValue, IConfigurationVersion } from "@/utilities/forms/ConfigurationFormProcessor";
import { TransitionProps } from "@mui/material/transitions";

const StickyTop = styled(Box)(({ theme }) => ({
    position: 'sticky',
    paddingBottom: theme.spacing(6),
    top: 48 /* header height */ + 24,
    
    '@media (min-width: 600px)': {
        top: 64 /* header height */ + 24,
    }
}));

export interface IConfigurationFormEditProps {
    loading?: boolean;
    trial?: ITrial;
    metadata?: ITrialFormMetadata;
}

const StickyTrigger = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: '-1px',  /* ➜ the trick */
    color: 'pink'
}));

const StickyBox = styled(Box)(({ theme }) => ({
    position: 'sticky',
    top: 0,

    '&.is-pinned': {
        paddingTop: 48 /* header height */ + 24,
    
        '@media (min-width: 600px)': {
            paddingTop: 64 /* header height */ + 24,
        },
        
        '& > div': {
            maxHeight: 'calc(100vh - (48px + 24px + 24px + 52px))',
    
            '@media (min-width: 600px)': {
                maxHeight: 'calc(100vh - (64px + 24px + 24px + 52px))'
            }
        }
    },


    '& > div': {
        maxHeight: 'calc(100vh - (48px + 24px + 24px + 130px))',

        '@media (min-width: 600px)': {
            maxHeight: 'calc(100vh - (64px + 24px + 24px + 130px))',
            //top: 64 /* header height */ + 24,
        },
    }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& > .MuiDialog-container > .MuiPaper-root': {
        background: theme.palette.background.default
    }
}));

const StickyBottom = styled(Box)(({ theme }) => ({
    position: 'sticky',
    textAlign: 'right', 
    top: 'calc(100vh - 76px)'
}));

const StickyTableOfContents = styled(Paper)(({ theme }) => ({
    overflow: 'auto',
}));

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
      children: ReactElement;
    },
    ref: Ref<unknown>,
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

const ConfigurationFormEdit: FC<IConfigurationFormEditProps> = ({
    loading,
    trial,
    metadata
}) => {
    const editContextObserver = useContext(ConfigurationFormEditContext);

    const [context, setContext] = useState(() => editContextObserver.get());

    useEffect(() => {
        setContext(editContextObserver.get());

        const unsubscribe = editContextObserver.subscribe((val) => {
            setContext(val)
        });

        return () => {
            unsubscribe();
        };
    }, [setContext, editContextObserver]);

    let form = null;
    
    if (context?.item.type === ConfigurationType.Value) {
        form = (
            <ConfigurationFormEditFieldValue
                context={context as IConfigurationFormEditContext<IConfigurationValue>}
            />
        )
    }
    else if (context?.item.type === ConfigurationType.Field) {
        form =  (
            <ConfigurationFormEditField
                context={context as IConfigurationFormEditContext<IConfigurationField>}
            />
        )
    }
    else if (context?.item.type === ConfigurationType.Section) {
        form =  (
            <ConfigurationFormEditSection
                context={context as IConfigurationFormEditContext<IConfigurationSection>}
            />
        )
    }
    else if (context?.item.type === ConfigurationType.Version) {
        form =  (
            <ConfigurationFormEditVersion
                context={context as IConfigurationFormEditContext<IConfigurationVersion>}
            />
        )
    }
    else if (context?.item.type === ConfigurationType.Form) {
        form =  (
            <ConfigurationFormEditForm
                context={context as IConfigurationFormEditContext<IConfigurationForm>}
            />
        )
    }
    else if (context?.item.type === ConfigurationType.Event) {
        form =  (
            <ConfigurationFormEditEvent
                context={context as IConfigurationFormEditContext<IConfigurationEvent>}
            />
        )
    }

    const handleClose = () => {
        editContextObserver.set(undefined);
    };

    const triggerRef = useRef<HTMLDivElement>(null);
    const stickyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = stickyRef.current;
        const trigger = triggerRef.current;
        

        if (el && trigger) {
            var doc = el.ownerDocument;

            const observer = new IntersectionObserver( 
                ([e]) => {
                    el.classList.toggle("is-pinned", e.intersectionRatio < 1)
                },
                { threshold: [1], root: doc, rootMargin: '0px 0px 0px 0px' }
            );
    
            observer.observe(trigger);

            return () => {
                observer.disconnect();
            }
        }
    }, [])

    return (
        <>
            <StickyTrigger ref={triggerRef}></StickyTrigger>
            <StickyBox ref={stickyRef}>
                <StyledDialog
                    fullScreen
                    open={!!form}
                    onClose={handleClose}
                    TransitionComponent={Transition}
                >
                    {form}
                </StyledDialog>
            </StickyBox>
            {
                !loading && (
                    <StickyBottom>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <ConfigurationFormPreview trial={trial} metadata={metadata} />
                            </Grid>
                            <Grid item xs={6}>
                                <FormSubmitButton
                                    color="primary"
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                >
                                    Save
                                </FormSubmitButton>
                            </Grid>
                        </Grid>
                    </StickyBottom>
                )
            }
        </>
    )
}

export default ConfigurationFormEdit;